import React, { useRef, useState } from 'react';
import { Check, ChevronDown, Cpu, Lock } from 'lucide-react';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';

/**
 * THE ROOM'S MODEL (MODES.md §3e) — right of the agent picker in Context: Agent default · Fast · Think · Reason.
 * ONE choice for the room, sticky for everyone (stored on the room, live via ROOM_MODEL_CHANGED); Ask AND Do answer
 * on it, overriding the network agent's own model in this room only (the network config is untouched). Only the
 * room's OWNER changes it (the server enforces it) — everyone else sees it, locked, with who set it.
 * Labels are capability tiers, never model names (vendor-agnostic; the tenant's tier mapping applies).
 */
export const CONTEXT_MODELS = [
  { tier: '', label: 'Agent default', hint: "Each agent's own model, as the network sets it" },
  { tier: 'fast', label: 'Fast', hint: 'Quick answers — lowest cost' },
  { tier: 'think', label: 'Think', hint: 'Balanced — the standard for most work' },
  { tier: 'reason', label: 'Reason', hint: 'Deepest reasoning — slower, highest cost' },
];

export function contextModelLabel(tier) {
  return (CONTEXT_MODELS.find((m) => m.tier === (tier || '')) || CONTEXT_MODELS[0]).label;
}

export default function ContextModelPicker({ value = '', canSet = false, setByName = '', onChange = null, disabled = false }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const locked = !canSet || typeof onChange !== 'function';
  const label = contextModelLabel(value);
  const who = setByName ? ` — set by ${setByName}` : '';
  const title = error || (locked
    ? `Model: ${label}${who}. Only the room's owner can change it.`
    : `Model for this room: ${label}${who}. Every agent answers on it here (Ask and Do).`);
  const pick = async (tier) => {
    setOpen(false);
    if (tier === (value || '')) return;
    setBusy(true); setError('');
    try { await onChange(tier); } catch (e) { setError((e && e.message) || "Couldn't change the model."); }
    setBusy(false);
  };
  return (
    <>
      <button ref={ref} type="button" data-testid="composer-context-model" data-tier={value || 'default'}
        aria-haspopup="menu" aria-expanded={open} title={title}
        disabled={disabled || busy || locked} onClick={() => setOpen((o) => !o)}
        className={`inline-flex max-w-[9rem] items-center gap-1 rounded-md border bg-white px-1.5 py-0.5 text-[11px] transition-colors disabled:cursor-not-allowed ${
          error ? 'border-rose-300 text-rose-700' : value ? 'border-indigo-200 text-indigo-700' : 'border-gray-200 text-gray-700'} ${
          locked ? 'opacity-80' : 'hover:bg-gray-50'}`}>
        {locked ? <Lock className="h-3 w-3 shrink-0" aria-hidden /> : <Cpu className="h-3 w-3 shrink-0" aria-hidden />}
        <span className="truncate">{busy ? 'Saving…' : label}</span>
        {locked ? null : <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />}
      </button>
      <PortalMenu anchorRef={ref} open={open && !locked} onClose={() => setOpen(false)} align="top-left">
        <div className="w-64 py-1 text-[12px]" role="menu" data-testid="composer-context-model-menu">
          <div className="px-3 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Model for this room</div>
          {CONTEXT_MODELS.map((m) => (
            <button key={m.tier || 'default'} type="button" role="menuitemradio" aria-checked={(value || '') === m.tier}
              data-testid={`composer-context-model-${m.tier || 'default'}`} onClick={() => pick(m.tier)}
              className="flex w-full items-start gap-2 px-3 py-1.5 text-left hover:bg-gray-50">
              <span className="mt-0.5 w-3.5 shrink-0 text-indigo-600">{(value || '') === m.tier ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}</span>
              <span className="min-w-0">
                <span className="block font-medium text-gray-900">{m.label}</span>
                <span className="block text-[11px] text-gray-500">{m.hint}</span>
              </span>
            </button>
          ))}
          <div className="mt-1 border-t border-gray-100 px-3 pt-1.5 text-[10.5px] leading-snug text-gray-400">
            Applies to everyone in this room, for Ask and Do. The network&apos;s agent settings don&apos;t change.
          </div>
        </div>
      </PortalMenu>
    </>
  );
}
